/**
 * Theme Constants
 *
 * Status colors and theme mode type used across the app.
 * Dynamic theme generation is handled by themeBuilder.ts.
 */

// Status colors for work orders.
// Brand-aligned semantic spectrum: values are drawn from the OceanSpace /
// OS Buddy palette (Figma "Oceanspace 0.2") so status chips/pills/kanban match
// the theme. These are intentionally fixed semantic colors (like channelColors)
// — they do not follow tenant primary/secondary overrides. `paused` stays
// violet because the brand palette has no purple and the state needs to remain
// visually distinct.
export const statusColors = {
  quote: '#A2A2A2', // Dark 6 — neutral grey (draft)
  pending: '#606060', // Dark 5 — grey (waiting)
  confirmed: '#228ACF', // primary — Blue Abyss
  vehicle_arrived: '#5BD5E6', // secondary — Blue Shallow
  in_progress: '#FFB92E', // warning — Yellow Accent 2
  paused: '#a855f7', // kept violet (no brand purple; keep distinct)
  waiting_parts: '#FFD52E', // warning.light — Yellow Accent 1
  work_completed: '#84AC2B', // success.dark
  ready_to_invoice: '#2FB6CC', // secondary.dark
  // Mid-blue between ready_to_invoice (cyan) and invoiced (violet) so the
  // billing sequence stays readable on a kanban board (#3941).
  on_consolidated_invoice: '#3F6FD8',
  invoiced: '#7C5CE6', // distinct from ready_to_invoice and completed (#1958)
  completed: '#A4D436', // success — Green Accent 2
  cancelled: '#FE4B09', // error — Red Accent 1; grey read as "waiting" (#3402)
  quote_rejected: '#FE4B09', // error — Red Accent 1
  // Legacy keys (status history may contain these old values)
  scheduled: '#228ACF', // primary
  waiting_approval: '#2FB6CC', // secondary.dark
  warranty_claim: '#228ACF', // primary
  disputed: '#FE4B09', // error
  invoicing_hold: '#D9971F', // warning.dark
} satisfies Record<string, string>;

/**
 * Communication channel colors.
 *
 * These are domain/brand colors (e.g. the official WhatsApp green) and are
 * intentionally kept visually consistent across light/dark. Centralized here so
 * every channel icon/chip references a single source of truth.
 */
export const channelColors = {
  whatsapp: '#25D366',
  sms: '#2196f3',
  email: '#f44336',
  internal: '#9e9e9e',
  portal: '#7e57c2',
} satisfies Record<string, string>;

/**
 * Neutral grey (Dark 5) used for inactive / past / placeholder UI surfaces that
 * are not tied to a specific status. Single source of truth so components stop
 * hardcoding the raw hex.
 */
export const neutralGrey = '#606060';

/**
 * Calendar accent colors not covered by the MUI palette.
 */
export const calendarColors = {
  /** Unassigned column / service block */
  unassigned: '#FFB92E', // warning — Yellow Accent 2
  /** Tentative (not yet confirmed) service item text */
  tentative: '#D9971F', // warning.dark
  /**
   * Hairline outline on calendar entries. Translucent black so it composites
   * into a slightly darker shade of whatever status color fills the block —
   * that keeps back-to-back entries separable on every status color and in
   * both themes, which a fixed light or dark line cannot do. Kept light so the
   * 1px stroke reads as a thin edge rather than a heavy frame.
   */
  itemBorder: 'rgba(0, 0, 0, 0.14)',
} satisfies Record<string, string>;

/**
 * Distinct hues used to color shift-pattern slots so adjacent slots are easy to
 * tell apart. Order matters: slots are assigned colors by index.
 */
export const shiftSlotColors = [
  '#228ACF', // primary
  '#A4D436', // success
  '#FFB92E', // warning
  '#FE4B09', // error
  '#5BD5E6', // secondary
  '#2FB6CC', // secondary.dark
  '#D9971F', // warning.dark
  '#606060', // grey (Dark 5)
] as const;

/**
 * Inspection / condition rating colors. Brand-aligned semantic spectrum reused
 * by condition indicators, inspection rows, and printed reports. Fixed (not
 * tenant-overridable) so a report looks the same everywhere.
 */
export const conditionColors = {
  good: '#A4D436', // success — Green Accent 2
  fair: '#FFD52E', // warning.light — Yellow Accent 1
  poor: '#FFB92E', // warning — Yellow Accent 2
  critical: '#FE4B09', // error — Red Accent 1
} satisfies Record<string, string>;

/**
 * Chart palette — distinct hues for data-visualization series (dashboards,
 * reports). Drawn from the brand palette where possible; order is significant.
 */
export const chartColors = [
  '#228ACF', // primary — Blue Abyss
  '#A4D436', // success
  '#FFB92E', // warning
  '#FE4B09', // error
  '#5BD5E6', // secondary
  '#2FB6CC', // secondary.dark
  '#D9971F', // warning.dark
  '#84AC2B', // success.dark
] as const;

/**
 * Revenue chart series (#3906): consumer / company / cash & card. The brand
 * hues fail colour-blind separation (green↔yellow ΔE 2 protan), so these are
 * a validated categorical set, stepped separately for the dark surface.
 */
export const revenueSeriesColors = {
  light: { consumer: '#eb6834', company: '#2a78d6', cashCard: '#1baf7a' },
  dark: { consumer: '#d95926', company: '#3987e5', cashCard: '#199e70' },
} as const;

export type ThemeMode = 'light' | 'dark';
